import * as React from 'react'

import {
  Alert,
  AlertProps,
  Box,
  Text,
  TogglerCheckbox,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Fields } from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/Fields'
import {
  WizardData,
  genericAmplitudeProperties,
} from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'
import WorkflowSelector from 'modules/processes/components/WorkflowSelector'

const isCompletedStep = (newData: WizardData) => {
  const validOnboardingProcess = !newData.enableEmployeeOnboarding || !!newData.onboardingProcessId

  return validOnboardingProcess
}

const GeneralSettingsStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [state, setState] = React.useState({
    startDateOpen: false,
    onboardingTemplateSelectorOpen: false,
    clickedOutsideTooltip: false,
  })

  const canAssignWorkflow = data.canAssignWorkflow || false
  const fields = data.fieldsSchema?.filter((field) => field.category === 'general_settings')
  const [permissionGroupField] =
    fields?.filter(
      (field) => field.fieldName === 'permissions_group_id' && data.canAssignPermissionGroup
    ) || []

  const onToggleSendInvitation = (sendInvitation: boolean | undefined) => {
    onDataChanged({ ...data, sendInvitation })
  }

  const updateWorkFlowSettings = (
    enableEmployeeOnboarding: boolean,
    onboardingProcessId: string | undefined
  ) => {
    onDataChanged({ ...data, enableEmployeeOnboarding, onboardingProcessId })
  }

  const onOnboardingWorkflowSelect = (onboardingProcessId: string | undefined) => {
    onDataChanged({ ...data, onboardingProcessId })
  }

  const handleToggle = (isOpen: boolean) => {
    setState({ ...state, onboardingTemplateSelectorOpen: isOpen })
  }

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
  }

  const getOnboardingTemplateAlertText = (): React.ReactNode => {
    if (!data?.onboardingProcessId)
      return i18n.t(
        'employee_form.wizard.onboarding_step.onboarding_template_select.alert.unselected'
      )

    return `✉️ ${i18n.t(
      'employee_form.wizard.onboarding_step.onboarding_template_select.alert.selected'
    )}`
  }

  const getOnboardingTemplateAlertColor = (): AlertProps['color'] => {
    if (state.onboardingTemplateSelectorOpen) return 'info'
    if (!data?.onboardingProcessId) return 'warning'

    return 'note'
  }

  useOnNext(() => {
    track(unifiedAddEmployeeEvents.addGeneralSettings, genericAmplitudeProperties(data))
  })

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('add_employee_modal.general_settings_step.section.general_settings')}
          </Text.Header2>
          {permissionGroupField && (
            <Fields
              key={permissionGroupField.fieldName}
              field={permissionGroupField}
              data={data}
              onChangeField={onChangeField}
            />
          )}
          {data.canInviteUserToFactorial && (
            <>
              <Box
                border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
                padding="s16"
                gap="s16"
                borderRadius={{ all: 'abs008' }}
                flexDirection="row"
                alignItems="flexStart"
                justifyContent="spaceBetween"
              >
                <Box gap="s8">
                  <Text.Normal weight="medium" color="grey1000">
                    {i18n.t('add_employee_modal.onboarding_info_step.fields.send_invitation.label')}
                  </Text.Normal>
                  <Text.Normal size="200">
                    {i18n.t(
                      'add_employee_modal.onboarding_info_step.fields.send_invitation.description'
                    )}
                  </Text.Normal>
                </Box>

                <TogglerCheckbox
                  value={data?.sendInvitation ?? false}
                  onChange={onToggleSendInvitation}
                />
              </Box>
              {data?.sendInvitation && (
                <WorkflowSelector
                  enableEmployeeOnboarding={!!data.enableEmployeeOnboarding}
                  updateWorkFlowSettings={updateWorkFlowSettings}
                  selectedWorkflow={data?.onboardingProcessId}
                  onWorkflowSelect={onOnboardingWorkflowSelect}
                  mandatory={data.enableEmployeeOnboarding}
                  onToggle={handleToggle}
                  canAssignWorkflow={canAssignWorkflow}
                />
              )}
            </>
          )}
        </Box>
      </Box>
      <Box flexDirection="column" paddingTop="s80" maxWidth="s240" paddingRight="s4">
        {data.canInviteUserToFactorial && (
          <>
            <Box paddingTop="s40">
              <Alert color="note">
                {i18n.t('employee_form.wizard.contract_step.contract_alert')}
              </Alert>
            </Box>
            {data?.sendInvitation && (
              <Box flexDirection="column" maxWidth="s240">
                <Box paddingTop="s8" height="s140">
                  {!data?.enableEmployeeOnboarding ? (
                    <Alert color="note">
                      {i18n.t('employee_form.wizard.onboarding_step.onboarding_card_toggle.alert')}
                    </Alert>
                  ) : (
                    <Box gap="s4">
                      <Alert color={getOnboardingTemplateAlertColor()}>
                        {getOnboardingTemplateAlertText()}
                      </Alert>
                      <Alert color="warning">
                        {i18n.t(
                          'employee_form.wizard.onboarding_step.onboarding_template_select.alert.time_warning'
                        )}
                      </Alert>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export const generalSettingsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.general_settings_step.title'),
  nextText: i18n.t('add_employee_modal.submit_button'),
  render: (props) => <GeneralSettingsStep {...props} />,
  isCompleted: isCompletedStep,
})
